import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import { useSort, SortTh } from "../components/SortableTh";
import {
  CircleNotch, MagnifyingGlass, Briefcase, CaretLeft, CaretRight, ArrowSquareOut,
} from "@phosphor-icons/react";

const STATUS_STYLE = {
  NOT_STARTED: "bg-neutral-100 text-neutral-600",
  PENDING: "bg-[#D97706]/10 text-[#D97706]",
  IN_PROGRESS: "bg-[#0055FF]/10 text-[#0055FF]",
  ARCHIVED: "bg-neutral-200 text-neutral-500",
  COMPLETED: "bg-[#059669]/10 text-[#059669]",
};
const fmtStatus = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const FILTERS = [
  { id: "active", label: "Active" },
  { id: "NOT_STARTED", label: "Not started" },
  { id: "PENDING", label: "Pending" },
  { id: "IN_PROGRESS", label: "In progress" },
  { id: "all", label: "All" },
];

export default function Jobs() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("active");
  const [page, setPage] = useState(1);
  const { sort, toggle } = useSort("due_date", "asc");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debounced, status, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs/list", { params: { page, limit: 50, search: debounced, status, sort_by: sort.by, sort_dir: sort.dir } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [page, debounced, status, sort]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const counts = data?.status_counts || {};
  const countFor = (id) => (id === "active" ? data?.active_total : id === "all"
    ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[id]);

  return (
    <Layout wide>
      <div data-testid="jobs-page">
        <div>
          <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">AroFlo</p>
          <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Jobs</h1>
          <p className="mt-2 max-w-3xl text-base text-neutral-600">
            All AroFlo jobs synced into Asset Hub. Showing active jobs by default — filter by status or search by job
            number, client, description or site.
          </p>
        </div>

        {/* Status filter pills + search */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2" data-testid="jobs-status-filters">
            {FILTERS.map((f) => {
              const n = countFor(f.id);
              return (
                <button
                  key={f.id}
                  data-testid={`jobs-filter-${f.id}`}
                  onClick={() => setStatus(f.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-all duration-200 ${
                    status === f.id ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-neutral-300 bg-white text-neutral-700 hover:border-[#0055FF]"
                  }`}
                >
                  {f.label}
                  {n != null && <span className={`rounded-full px-1.5 text-xs ${status === f.id ? "bg-white/20" : "bg-neutral-100 text-neutral-500"}`}>{n}</span>}
                </button>
              );
            })}
          </div>
          <div className="relative w-full max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="jobs-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search job #, client, site…"
              className="w-full rounded-sm border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#0055FF]"
            />
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
          ) : items.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center gap-2 text-neutral-400" data-testid="jobs-empty">
              <Briefcase size={32} /> No jobs match this filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                    <SortTh label="Job #" field="job_number" sort={sort} onSort={toggle} />
                    <SortTh label="Description" field="name" sort={sort} onSort={toggle} />
                    <SortTh label="Client" field="client" sort={sort} onSort={toggle} />
                    <SortTh label="Site" field="site" sort={sort} onSort={toggle} />
                    <SortTh label="Type" field="task_type" sort={sort} onSort={toggle} />
                    <th className="px-4 py-3 text-left">Technician</th>
                    <SortTh label="Due" field="due_date" sort={sort} onSort={toggle} />
                    <SortTh label="Status" field="status" sort={sort} onSort={toggle} />
                    <th className="px-4 py-3 text-right" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((j) => (
                    <tr
                      key={j.job_id || j.job_number}
                      data-testid={`job-row-${j.job_number}`}
                      onClick={() => navigate(`/jobs/${j.job_id}`)}
                      className="cursor-pointer border-b border-neutral-100 transition-colors hover:bg-[#0055FF]/[0.04]"
                    >
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-[#0055FF]">{j.job_number}</td>
                      <td className="max-w-[260px] truncate px-4 py-3 font-medium text-[#0A0A0A]" title={j.name}>{j.name || "—"}</td>
                      <td className="max-w-[180px] truncate px-4 py-3 text-neutral-600" title={j.client}>{j.client || "—"}</td>
                      <td className="max-w-[160px] truncate px-4 py-3 text-neutral-500" title={j.site}>{j.site || "—"}</td>
                      <td className="px-4 py-3 text-neutral-500">{j.task_type || "—"}</td>
                      <td className="max-w-[150px] truncate px-4 py-3 text-neutral-600" title={(j.technicians || []).join(", ")}>{(j.technicians || []).filter(Boolean).join(", ") || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-neutral-500">{j.due_date || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-sm px-2 py-0.5 text-xs font-bold ${STATUS_STYLE[j.status] || "bg-neutral-100 text-neutral-500"}`}>{fmtStatus(j.status)}</span>
                      </td>
                      <td className="px-4 py-3 text-right"><ArrowSquareOut size={15} className="ml-auto text-neutral-300" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {data && data.pages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-neutral-500" data-testid="jobs-count">{data.count.toLocaleString()} jobs · page {data.page} of {data.pages}</p>
            <div className="flex gap-2">
              <button data-testid="jobs-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="inline-flex items-center gap-1 rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-40">
                <CaretLeft size={14} weight="bold" /> Prev
              </button>
              <button data-testid="jobs-next" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-40">
                Next <CaretRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}

        <PageAdvice items={[
          "Click any job to open its full detail, documents and asset condition report.",
          "Active jobs are those not Archived, Completed, Cancelled or Closed.",
        ]} />
      </div>
    </Layout>
  );
}
